/**
 * useConsent — React hook for DPDP consent state management
 *
 * Manages:
 *  - Reading dpdp_consent_id and dpdp_consent_rejected cookies
 *  - Caching consent state in localStorage for fast UI rendering
 *  - Sync with backend consent_records table on app load
 *  - Granting and withdrawing consent
 *
 * NOTE: The HttpOnly cookie set by the backend IS the authoritative consent proof.
 * localStorage is used only as a render-time cache to avoid redundant API calls.
 * On every page load we attempt to reconcile with the backend.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import Cookies from "js-cookie";
import {
  getConsentStatus,
  getConsentHistory,
  grantConsent,
  withdrawConsent,
  getActiveNotice,
} from "@/services/dpdpApi";

// ---------------------------------------------------------------------------
// Cookie / localStorage keys
// ---------------------------------------------------------------------------

const CONSENT_ID_COOKIE = "dpdp_consent_id";
const CONSENT_REJECTED_COOKIE = "dpdp_consent_rejected";
const CONSENT_CACHE_KEY = "dpdp_consent_cache";
const REJECT_EXPIRY_DAYS = 180; // 6 months

// ---------------------------------------------------------------------------
// Purpose definitions — maps purpose codes to human-readable info
// ---------------------------------------------------------------------------

export const CONSENT_PURPOSES = [
  {
    code: "account_creation",
    label: "Account Creation",
    description: "Creating and managing your Right Doers World account",
    required: true,
    canWithdraw: false,
    category: "essential",
  },
  {
    code: "platform_service",
    label: "Platform Service",
    description: "Providing and improving core platform features",
    required: true,
    canWithdraw: false,
    category: "essential",
  },
  {
    code: "job_matching",
    label: "Job Matching",
    description: "Matching you with relevant job opportunities",
    required: false,
    canWithdraw: true,
    category: "functional",
  },
  {
    code: "psychometric_assessment",
    label: "Psychometric Assessment",
    description: "Big 5 personality assessments for career guidance",
    required: false,
    canWithdraw: true,
    category: "functional",
  },
  {
    code: "voice_processing",
    label: "Voice Processing",
    description: "Processing voice commands via AI (audio not stored)",
    required: false,
    canWithdraw: true,
    category: "functional",
  },
  {
    code: "ai_chat",
    label: "AI Chat (AIMEE)",
    description: "AI-powered chatbot conversations",
    required: false,
    canWithdraw: true,
    category: "functional",
  },
  {
    code: "marketing_email",
    label: "Email Marketing",
    description: "Promotional emails and newsletters",
    required: false,
    canWithdraw: true,
    category: "marketing",
  },
  {
    code: "marketing_sms",
    label: "SMS Marketing",
    description: "Promotional SMS messages",
    required: false,
    canWithdraw: true,
    category: "marketing",
  },
  {
    code: "marketing_whatsapp",
    label: "WhatsApp Marketing",
    description: "Promotional messages via WhatsApp",
    required: false,
    canWithdraw: true,
    category: "marketing",
  },
  {
    code: "third_party_sharing",
    label: "Third-Party Sharing",
    description: "Sharing data with vetted third-party partners",
    required: false,
    canWithdraw: true,
    category: "marketing",
  },
  {
    code: "analytics",
    label: "Analytics",
    description: "Platform usage analytics and insights",
    required: false,
    canWithdraw: true,
    category: "analytics",
  },
  {
    code: "legal_compliance",
    label: "Legal Compliance",
    description: "Meeting legal and regulatory obligations",
    required: true,
    canWithdraw: false,
    category: "essential",
  },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Map purpose code → label */
export const getPurposeLabel = (code) =>
  CONSENT_PURPOSES.find((p) => p.code === code)?.label ?? code;

/** Check if a purpose has been granted in the status response */
export const isPurposeGranted = (status, purposeCode) =>
  status?.purposes?.some(
    (p) => p.purpose_code === purposeCode && p.status === "granted"
  ) ?? false;

/** Get granted purposes from a status response */
export const getGrantedPurposes = (status) =>
  new Set(
    (status?.purposes ?? [])
      .filter((p) => p.status === "granted")
      .map((p) => p.purpose_code)
  );

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useConsent() {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [consentStatus, setConsentStatus] = useState(null);
  const [consentHistory, setConsentHistory] = useState([]);
  const [activeNotice, setActiveNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [granting, setGranting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchAttempted = useRef(false);

  /** Attempt to sync with backend on mount */
  const syncWithBackend = useCallback(async () => {
    try {
      // Pass data_principal_id so anonymous users can also retrieve their status
      const dpId = _getUserIdentifier();
      const [status, history] = await Promise.all([
        getConsentStatus(dpId).catch(() => null),
        getConsentHistory(dpId).catch(() => []),
      ]);
      setConsentStatus(status);
      setConsentHistory(history?.events ?? []);
      // Cache in localStorage
      try {
        localStorage.setItem(
          CONSENT_CACHE_KEY,
          JSON.stringify({ status, history: history?.events ?? [], ts: Date.now() })
        );
      } catch {
        // localStorage full — non-fatal
      }
    } catch (err) {
      console.warn("[Consent] Backend sync failed:", err);
    }
  }, []);

  /** Determine if banner should be shown */
  useEffect(() => {
    if (fetchAttempted.current) return;
    fetchAttempted.current = true;

    const init = async () => {
      setLoading(true);
      try {
        // 1. Check cookies first (authoritative)
        const consentId = Cookies.get(CONSENT_ID_COOKIE);
        const consentRejected = Cookies.get(CONSENT_REJECTED_COOKIE);

        if (consentId || consentRejected) {
          // Cookie exists — try to sync with backend
          await syncWithBackend();
        } else {
          // No cookie — banner must be shown
          setBannerVisible(true);
        }

        // 2. Load cached state for fast initial render
        try {
          const cached = JSON.parse(localStorage.getItem(CONSENT_CACHE_KEY) ?? "null");
          if (cached?.status) {
            setConsentStatus(cached.status);
            setConsentHistory(cached.history ?? []);
            // Cache older than 5 minutes — refresh
            if (Date.now() - cached.ts > 5 * 60 * 1000) {
              syncWithBackend();
            }
          }
        } catch {
          // Corrupted cache — ignore
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [syncWithBackend]);

  /** Load the active notice in the user's preferred language */
  const loadNotice = useCallback(async (language = "en") => {
    try {
      const notice = await getActiveNotice(language);
      setActiveNotice(notice);
      return notice;
    } catch (err) {
      console.warn("[Consent] Failed to load notice:", err);
      return null;
    }
  }, []);

  /** Accept ALL purposes — essential + non-essential */
  const acceptAll = useCallback(
    async (noticeId) => {
      setGranting(true);
      try {
        // Grant all configurable purposes (essential are always active server-side)
        const configurablePurposes = CONSENT_PURPOSES
          .filter((p) => !p.required)
          .map((p) => p.code);

        const result = await grantConsent({
          data_principal_id: _getUserIdentifier(),
          purposes: configurablePurposes,
          notice_id: noticeId || activeNotice?.notice_id || "",
          channel: "web",
          is_minor: _isMinor(),
        });

        // The backend sets the HttpOnly cookie — we just need to update local state
        setConsentStatus((prev) => ({
          ...prev,
          purposes: [
            ...(prev?.purposes ?? []),
            ...configurablePurposes.map((code) => ({
              purpose_code: code,
              status: "granted",
            })),
          ],
        }));

        setBannerVisible(false);
        Cookies.set(CONSENT_ID_COOKIE, result.consent_id, {
          expires: 365,
          sameSite: "Strict",
          secure: window.location.protocol === "https:",
        });

        return result;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setGranting(false);
      }
    },
    [activeNotice]
  );

  /** Accept essential purposes only, explicitly reject non-essential */
  const acceptOnlyEssential = useCallback(
    async (noticeId) => {
      setGranting(true);
      try {
        // Explicitly withdraw all non-essential purposes
        await withdrawConsent({
          data_principal_id: _getUserIdentifier(),
          all_purposes: false,
          purposes: CONSENT_PURPOSES
            .filter((p) => !p.required)
            .map((p) => p.code),
        }).catch(() => {
          // If no prior consent exists, withdrawal will 404 — that's fine
        });

        setBannerVisible(false);
        // Keep the consent cookie but mark rejection
        Cookies.set(CONSENT_REJECTED_COOKIE, "1", {
          expires: REJECT_EXPIRY_DAYS,
          sameSite: "Strict",
          secure: window.location.protocol === "https:",
        });

        await syncWithBackend();
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setGranting(false);
      }
    },
    [activeNotice, syncWithBackend]
  );

  /** Accept only specific purposes (from preferences panel) */
  const acceptPurposes = useCallback(
    async (purposeCodes, noticeId) => {
      setGranting(true);
      try {
        const result = await grantConsent({
          data_principal_id: _getUserIdentifier(),
          purposes: purposeCodes,
          notice_id: noticeId || activeNotice?.notice_id || "",
          channel: "web",
          is_minor: _isMinor(),
        });

        await syncWithBackend();
        Cookies.set(CONSENT_ID_COOKIE, result.consent_id, {
          expires: 365,
          sameSite: "Strict",
          secure: window.location.protocol === "https:",
        });

        return result;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setGranting(false);
      }
    },
    [activeNotice, syncWithBackend]
  );

  /** Reject all non-essential purposes */
  const rejectAll = useCallback(async () => {
    setGranting(true);
    try {
      // Reject by withdrawing all non-essential purposes
      await withdrawConsent({
        data_principal_id: _getUserIdentifier(),
        all_purposes: true,
      }).catch(() => {
        // If no prior consent exists, withdrawal will 404 — that's fine
      });

      setBannerVisible(false);
      Cookies.set(CONSENT_REJECTED_COOKIE, "1", {
        expires: REJECT_EXPIRY_DAYS,
        sameSite: "Strict",
        secure: window.location.protocol === "https:",
      });
    } catch (err) {
      setError(err);
    } finally {
      setGranting(false);
    }
  }, []);

  /** Withdraw specific purposes (from Privacy Centre) */
  const withdrawPurposes = useCallback(
    async (purposeCodes) => {
      setWithdrawing(true);
      try {
        const result = await withdrawConsent({
          data_principal_id: _getUserIdentifier(),
          purposes: purposeCodes,
        });

        await syncWithBackend();
        return result;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setWithdrawing(false);
      }
    },
    [syncWithBackend]
  );

  /** Force-show the banner (e.g., for re-consent) */
  const showBanner = useCallback(() => {
    setBannerVisible(true);
  }, []);

  /** Check if a specific purpose is granted */
  const hasConsent = useCallback(
    (purposeCode) => {
      return isPurposeGranted(consentStatus, purposeCode);
    },
    [consentStatus]
  );

  return {
    // State
    bannerVisible,
    consentStatus,
    consentHistory,
    activeNotice,
    loading,
    error,
    granting,
    withdrawing,

    // Derived
    hasConsent,
    isPurposeGranted: (code) => isPurposeGranted(consentStatus, code),
    getGrantedPurposes: () => getGrantedPurposes(consentStatus),

    // Actions
    loadNotice,
    acceptAll,
    acceptOnlyEssential,
    acceptPurposes,
    rejectAll,
    withdrawPurposes,
    showBanner,
    syncWithBackend,
  };
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function _getUserIdentifier() {
  // Use the user ID from localStorage if available, otherwise use a session ID
  try {
    const user = JSON.parse(localStorage.getItem("rdw_user") ?? "null");
    return user?.id || user?.sub || user?.email || `anon_${Date.now()}`;
  } catch {
    return `anon_${Date.now()}`;
  }
}

function _isMinor() {
  try {
    const user = JSON.parse(localStorage.getItem("rdw_user") ?? "null");
    // Use authoritative is_minor field if available (set by backend from DOB)
    if (typeof user?.is_minor === "boolean") {
      return user.is_minor;
    }
    // Fallback heuristic using age_group
    const ageGroup = user?.age_group || user?.ageGroup || "";
    return ageGroup.startsWith("8-") || ageGroup.startsWith("9-") || ageGroup.startsWith("1");
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Restricted purposes for minors (DPDP Section 10)
// ---------------------------------------------------------------------------

export const RESTRICTED_FOR_MINORS = new Set([
  "photo_upload",
  "public_comments",
  "marketing_email",
  "marketing_sms",
  "marketing_whatsapp",
  "third_party_sharing",
]);

export function isPurposeRestrictedForMinor(purposeCode) {
  return RESTRICTED_FOR_MINORS.has(purposeCode);
}

export function canAccessPurpose(status, purposeCode, user) {
  if (!isPurposeRestrictedForMinor(purposeCode)) {
    return true;
  }
  const isMinor = (() => {
    if (typeof user?.is_minor === "boolean") return user.is_minor;
    const ageGroup = user?.age_group || "";
    return ageGroup.startsWith("8-");
  })();
  if (!isMinor) return true;
  return status?.parent_verified === true;
}
